import { useColorMode, useEventListener } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { UserRole } from '@/enums/user'
import type { IUser } from '@/types/User'

export const useAppStore = defineStore('app', () => {
    const JWT          = ref(localStorage.getItem('JWT') || '')
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    const userData     = ref<IUser>()

    // Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark() —
    // never a second source of truth. Two modes only, 'light' | 'dark' — no System
    // option in the UI. Without `emitAuto` the ref always reads the RESOLVED mode, so a
    // first visit (nothing stored yet) shows whatever the OS prefers. See theme.md.
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme' })

    const getJWT         = () => JWT.value
    const isLoggedIn     = () => !!JWT.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    const isAdmin            = computed(() => userData.value?.role === UserRole.Admin)
    const mustChangePassword = computed(() => !!userData.value?.must_change_password)

    // Keep the breakpoint LIVE — bound to the store's effect scope.
    useEventListener(window, 'resize', () => { windowWidth.value = window.innerWidth })

    // Sets a JWT cookie so that API requests for images/PDFs loaded via <img src> or
    // <a href> are authenticated — browsers cannot set Authorization headers for those.
    const setJWTCookie = (jwt: string) => {
        if (jwt) {
            document.cookie = `JWT=${jwt}; path=/; SameSite=Lax`
        } else {
            document.cookie = 'JWT=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax'
        }
    }

    setJWTCookie(JWT.value)

    const setLogin = (jwt: string) => {
        localStorage.setItem('JWT', jwt)
        JWT.value = jwt
        setJWTCookie(jwt)
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        JWT.value = ''
        userData.value = undefined
        setJWTCookie('')
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => { loaderActive.value = Math.max(0, loaderActive.value - 1) }

    /**
     * Loads the signed-in operator once per page load. The JWT survives a refresh but the
     * user record does not, and role and must_change_password drive routing — so the
     * router guard waits on this before deciding anything.
     */
    const ensureUserLoaded = async (): Promise<boolean> => {
        if (userData.value) return true
        if (!JWT.value) return false

        const { useFetchStore } = await import('@/stores/fetch')
        const { statusCode, data } = await useFetchStore().fetch('/me').get().json<IUser>()

        if (statusCode.value !== 200 || !data.value) return false

        userData.value = data.value

        return true
    }

    return {
        JWT, userData, loaderActive, colorMode, isAdmin, mustChangePassword,
        getJWT, isLoggedIn, isLoaderActive, isMobile,
        setLogin, setLogout, startLoading, stopLoading, ensureUserLoaded,
    }
})
